// Adherence client. Tracks today's check-offs in local state and
// hits the API to persist + load the daily streak. Optimistic UI:
// toggling a check happens locally first, then syncs.

import { useCallback, useEffect, useState } from 'react';
import { useAuthedFetch } from './useAuthedFetch';

export type AdherenceSlot = 'morning' | 'noon' | 'evening' | 'bedtime' | 'asneeded';

export interface AdherenceTodayItem {
  medication_id: string;
  slot: AdherenceSlot;
  takenOn: string;
  status: 'taken' | 'skipped';
}

// Stable key helper outside the hook so callbacks do not need it in deps.
function key(medId: string, slot: AdherenceSlot): string {
  return `${medId}:${slot}`;
}

export function useAdherence() {
  const authedFetch = useAuthedFetch();
  const [today, setToday] = useState<Map<string, 'taken' | 'skipped'>>(new Map());
  const [streak, setStreak] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [todayRes, streakRes] = await Promise.all([
        authedFetch('/api/adherence/today'),
        authedFetch('/api/adherence/streak'),
      ]);
      const t = (await todayRes.json()) as {
        ok: boolean;
        data?: { items: AdherenceTodayItem[] };
      };
      const s = (await streakRes.json()) as { ok: boolean; data?: { streak: number } };
      if (t.ok && t.data) {
        const m = new Map<string, 'taken' | 'skipped'>();
        for (const item of t.data.items) m.set(key(item.medication_id, item.slot), item.status);
        setToday(m);
      }
      if (s.ok && s.data) setStreak(s.data.streak);
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggle = useCallback(
    async (medicationId: string, slot: AdherenceSlot) => {
      const k = key(medicationId, slot);
      const current = today.get(k);
      const next = current === 'taken' ? null : 'taken';
      setToday((prev) => {
        const m = new Map(prev);
        if (next) m.set(k, next);
        else m.delete(k);
        return m;
      });
      try {
        if (next === null) {
          await authedFetch('/api/adherence', {
            method: 'DELETE',
            body: JSON.stringify({ medicationId, slot }),
          });
        } else {
          await authedFetch('/api/adherence', {
            method: 'POST',
            body: JSON.stringify({ medicationId, slot, status: 'taken' }),
          });
        }
        void refresh();
      } catch {
        // revert optimistic update on failure
        setToday((prev) => {
          const m = new Map(prev);
          if (current) m.set(k, current);
          else m.delete(k);
          return m;
        });
      }
    },
    [authedFetch, refresh, today],
  );

  const isTaken = useCallback(
    (medicationId: string, slot: AdherenceSlot): boolean => {
      return today.get(key(medicationId, slot)) === 'taken';
    },
    [today],
  );

  return { isTaken, toggle, streak, loading };
}
